import Block from "../block";
import {WebsiteContext} from "../../index";
import {aspectRatio} from "../../utils/block-utils";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import axios from "axios";
import Spinner from "../../components/spinner";
import Currency from "@autocx/forms/src/controls/currency";
import classNames from "classnames";
import {formatDateToFriendlyDate} from "./account-date-utils";
import {Image} from "../../components/image";
import {subscribeToPublicSalesEvents} from "../../realtime/public-sales-events";

const ACTIVITY_TABS = [
    {key: "active", label: "Active"},
    {key: "winning", label: "Winning"},
    {key: "outbid", label: "Outbid"},
    {key: "won", label: "Won"},
    {key: "unsuccessful", label: "Unsuccessful"},
    {key: "all", label: "All"},
];

const ACTIVITY_REFRESH_DELAY_MS = 250;
const ACTIVITY_LAG_RETRY_DELAY_MS = 1200;
const EMPTY_ACTIVITY_DATA = [];

function normaliseKey(value) {
    return String(value || "").trim();
}

function normaliseStatus(value) {
    const status = normaliseKey(value);
    return status ? status.charAt(0).toLowerCase() + status.slice(1) : "";
}

function isSaleCancelled(activity) {
    return normaliseKey(activity?.lotStatus).toLowerCase().replace(/[\s_-]+/g, "") === "salefellthrough";
}

function getEffectiveActivityStatus(activity) {
    return isSaleCancelled(activity) ? "saleCancelled" : normaliseStatus(activity?.activityStatus);
}

function normaliseMethod(activity) {
    return normaliseKey(activity?.sellingMethod).toLowerCase();
}

function isTender(activity) {
    return normaliseMethod(activity) === "tender";
}

function isAuction(activity) {
    return normaliseMethod(activity) === "auction";
}

function shouldRefreshFromRealtime(activity) {
    const status = getEffectiveActivityStatus(activity);
    return status === "winning"
        || status === "outbid"
        || status === "offerSubmitted"
        || status === "pendingOutcome";
}

function getActiveTab(searchParams) {
    const tab = String(searchParams.get("tab") || "active").toLowerCase();
    return ACTIVITY_TABS.some(option => option.key === tab) ? tab : "active";
}

function buildActivityUrl(baseUrl, tab) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set("tab", tab);
    url.searchParams.set("pageNumber", "1");
    url.searchParams.set("pageSize", "100");
    return `${url.pathname}${url.search}${url.hash}`;
}

function getPaginationTotalItems(response, fallback = 0) {
    const headers = response?.headers || {};
    const value = headers["x-paging-totalitems"]
        ?? headers["X-Paging-TotalItems"]
        ?? headers.get?.("x-paging-totalitems")
        ?? headers.get?.("X-Paging-TotalItems");
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function getActivityResponseData(response) {
    const data = response?.data;
    if (Array.isArray(data)) {
        return {
            items: data,
            counts: null
        };
    }

    return {
        items: Array.isArray(data?.items) ? data.items : Array.isArray(data?.Items) ? data.Items : [],
        counts: data?.counts || data?.Counts || null
    };
}

function normaliseActivityCounts(counts) {
    if (!counts) return null;

    return ACTIVITY_TABS.reduce((result, tab) => {
        const pascalKey = tab.key.charAt(0).toUpperCase() + tab.key.slice(1);
        const rawValue = counts[tab.key] ?? counts[pascalKey];
        const value = Number.parseInt(rawValue, 10);

        if (Number.isFinite(value)) {
            result[tab.key] = value;
        }

        return result;
    }, {});
}

function getStatusLabel(activity) {
    const status = getEffectiveActivityStatus(activity);
    if (status === "winning") return activity?.leadingStatusLabel || "You Are Winning";
    if (status === "outbid") return activity?.outbidStatusLabel || "You've Been Outbid";
    if (status === "offerSubmitted") return "Offer Submitted";
    if (status === "won") return isTender(activity) ? "Offer Accepted" : "Won";
    if (status === "unsuccessful") return "Unsuccessful";
    if (status === "pendingOutcome") return "Awaiting Result";
    if (status === "saleCancelled") return "Sale Cancelled";
    return status || "Activity";
}

function getAction(activity) {
    const status = getEffectiveActivityStatus(activity);
    if (!activity?.actionUrl) return null;
    if (status === "won" && activity?.orderUuid) return {label: "View Order", url: activity.actionUrl};
    if (status === "outbid") return {label: "Increase Bid", url: activity.actionUrl};
    if (status === "winning" || status === "offerSubmitted") return {label: "View Listing", url: activity.actionUrl};
    return null;
}

function getEventTypeLabel(activity) {
    return normaliseKey(activity?.salesEventTypeTitle) || (isTender(activity) ? "Tender" : "Auction");
}

function formatDate(value) {
    if (!value) return null;
    try {
        return formatDateToFriendlyDate(value);
    } catch (_error) {
        return null;
    }
}

function formatBidCount(activity) {
    const count = Number(activity?.totalBids || 0);
    const noun = isTender(activity) ? "offer" : "bid";
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function isActivityBehindRealtimeSummary(items, summary) {
    const productId = normaliseKey(summary?.productUuid).toLowerCase();
    if (!productId || !Array.isArray(items)) return false;

    const summaryLotId = Number(summary?.lotId);
    const hasSummaryLotId = Number.isSafeInteger(summaryLotId) && summaryLotId > 0;
    const activity = items.find(item => normaliseKey(item?.productId).toLowerCase() === productId
        && (!hasSummaryLotId || Number(item?.lotId) === summaryLotId));
    if (!activity) return false;

    const currentAmount = Number(activity?.currentAmount ?? 0);
    const highestBid = Number(summary?.highestBid);
    if (Number.isFinite(highestBid) && highestBid > 0 && (!Number.isFinite(currentAmount) || currentAmount < highestBid)) {
        return true;
    }

    const totalBids = Number(activity?.totalBids ?? 0);
    const summaryTotalBids = Number(summary?.totalBids);
    return Number.isFinite(summaryTotalBids)
        && summaryTotalBids > 0
        && (!Number.isFinite(totalBids) || totalBids < summaryTotalBids);
}

function getLiveActivityCheckpoint(summary) {
    // A public final result never acknowledges private winner/order/payment readiness.
    // Only unchanged live auction checkpoints may reuse a successful private read.
    if (normaliseStatus(summary?.lotStatus) !== "open"
        || normaliseKey(summary?.sellingMethod).toLowerCase() !== "auction"
        || !normaliseKey(summary?.publicStateVersion)
        || !Number.isSafeInteger(Number(summary?.lotId))
        || Number(summary.lotId) <= 0
        || !(Date.parse(summary?.endDate) > Date.now())) return null;

    return JSON.stringify([normaliseKey(summary.productUuid).toLowerCase(), summary.lotId,
        summary.publicStateVersion, summary.lotStatus, summary.endDate,
        summary.highestBid, summary.totalBids, summary.reserveMet]);
}

function canAcknowledgeLiveActivity(items, summary) {
    const activity = items.find(item => Number(item?.lotId) === Number(summary?.lotId)
        && normaliseKey(item?.productId).toLowerCase() === normaliseKey(summary?.productUuid).toLowerCase());
    return isAuction(activity)
        && ["winning", "outbid"].includes(getEffectiveActivityStatus(activity))
        && Number(activity.currentAmount) === Number(summary.highestBid)
        && Number(activity.totalBids) === Number(summary.totalBids);
}

function isFinalActivity(activity) {
    const status = getEffectiveActivityStatus(activity);
    return status === "won" || status === "unsuccessful" || status === "pendingOutcome" || status === "saleCancelled";
}

function getCurrentAmountLabel(activity) {
    if (isTender(activity)) return "Current Offer";
    return isFinalActivity(activity) ? "Final Bid" : "Current Bid";
}

function getActivityAmounts(activity) {
    const status = getEffectiveActivityStatus(activity);
    const yourAmount = activity?.yourAmount;
    const currentAmount = activity?.currentAmount;
    const hasYourAmount = yourAmount !== null && yourAmount !== undefined;
    const hasCurrentAmount = currentAmount !== null && currentAmount !== undefined;

    if (status === "won") {
        if (isTender(activity)) {
            return hasYourAmount ? [{label: "Accepted Offer", amount: yourAmount}] : [];
        }

        const amount = hasCurrentAmount ? currentAmount : yourAmount;
        return amount !== null && amount !== undefined ? [{label: "Winning Bid", amount}] : [];
    }

    if (isTender(activity)) {
        return hasYourAmount ? [{label: "Your Offer", amount: yourAmount}] : [];
    }

    const amounts = [];
    if (hasYourAmount) {
        amounts.push({label: "Your Bid", amount: yourAmount});
    }

    if (hasCurrentAmount && (!hasYourAmount || currentAmount !== yourAmount || !isFinalActivity(activity))) {
        amounts.push({label: getCurrentAmountLabel(activity), amount: currentAmount});
    }

    return amounts;
}

function ActivityAmount({label, amount, context}) {
    if (amount === null || amount === undefined) return null;

    return (
        <div className="min-w-[7.5rem]">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
            <div className="mt-0.5 text-base font-bold text-gray-900">
                <Currency.DisplayValue
                    currency={context.currency}
                    country={context.country}
                    value={amount}
                />
            </div>
        </div>
    );
}

function ActivityRow({activity, context}) {
    const status = getEffectiveActivityStatus(activity);
    const action = getAction(activity);
    const endDate = formatDate(activity?.endDate);
    const imageAspectRatio = {values: {options: {imageAspectRatio: context?.website?.store?.productCardAspectRatio}}};
    const eventTypeLabel = getEventTypeLabel(activity);
    const amounts = getActivityAmounts(activity);

    return (
        <article className="container-padding-half section-grey rounded-theme flex flex-col gap-4 mb-6 sm:flex-row sm:items-center">
            <div className="inline-flex flex-col w-full sm:flex-1">
                <span className="text-xs section-black font-bold rounded-full px-3 py-1 uppercase w-fit mb-2">
                    {getStatusLabel(activity)}
                </span>
                <span className="text-base text-gray-900 font-bold leading-tight">
                    {activity?.productTitle || "Untitled Listing"}
                </span>
                {eventTypeLabel && (
                    <span className="mt-1 text-base text-gray-700">
                        {eventTypeLabel}
                    </span>
                )}
                {amounts.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-3">
                        {amounts.map(amount => (
                            <ActivityAmount
                                key={amount.label}
                                label={amount.label}
                                amount={amount.amount}
                                context={context}
                            />
                        ))}
                    </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-base text-gray-700">
                    <span>{formatBidCount(activity)}</span>
                    {endDate && (
                        <>
                            <span aria-hidden="true">•</span>
                            <span>{status === "pendingOutcome" || status === "unsuccessful" || status === "won" || status === "saleCancelled" ? "Ended" : "Ends"} {endDate}</span>
                        </>
                    )}
                </div>
                {action && (
                    <a
                        href={action.url}
                        className="font-bold text-base mt-2"
                    >
                        {action.label}
                    </a>
                )}
            </div>

            <div className="w-full shrink-0 sm:w-40 md:w-48">
                <Image
                    pictureClassName="block w-full"
                    alt={"Image of " + (activity?.productTitle || "listing")}
                    width={220}
                    src={activity?.productImageUrl}
                    className={classNames("rounded-theme object-cover object-center w-full", aspectRatio(imageAspectRatio))}
                />
            </div>
        </article>
    );
}
export default function AccountBidsOffers(props) {
    const [activityDataByTab, setActivityDataByTab] = useState({});
    const [activityCounts, setActivityCounts] = useState({});
    const [loadedTabs, setLoadedTabs] = useState({});
    const [loading, setLoading] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const refreshTimeoutRef = useRef(null);
    const lagRetryTimeoutRef = useRef(null);
    const latestSummaryRef = useRef(null);
    const acknowledgedCheckpointsRef = useRef(new Map());
    const requestSequenceRef = useRef(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const context = useContext(WebsiteContext);
    const activeTab = getActiveTab(searchParams);
    const activityUrl = props?.profile?.customer?._links?.salesEventActivity?.href;
    const activityData = activityDataByTab[activeTab] || EMPTY_ACTIVITY_DATA;
    const activeTabLoaded = Boolean(loadedTabs[activeTab]);

    const emptyMessage = useMemo(() => {
        if (activeTab === "active") return "No active bids or offers yet.";
        if (activeTab === "winning") return "No winning auctions yet.";
        if (activeTab === "outbid") return "No outbid auctions yet.";
        if (activeTab === "won") return "No successful bids or offers yet.";
        if (activeTab === "unsuccessful") return "No unsuccessful bids or offers yet.";
        return "No bids or offers to display.";
    }, [activeTab]);

    useEffect(() => {
        document.title = props?.title;
    }, [props?.title]);

    const fetchActivity = useCallback(({
        showDelayedLoader = true,
        latestSummary = null,
        retryOnPotentialLag = false
    } = {}) => {
        if (!activityUrl) {
            setActivityDataByTab({});
            setActivityCounts({});
            setLoadedTabs({[activeTab]: true});
            setLoading(false);
            setShowLoader(false);
            return () => {};
        }

        let cancelled = false;
        const requestSequence = ++requestSequenceRef.current;
        let timer = null;

        if (showDelayedLoader) {
            setLoading(true);
            setShowLoader(false);

            timer = setTimeout(() => {
                if (cancelled) return;
                setShowLoader(true);
            }, 400);
        }

        axios.get(buildActivityUrl(activityUrl, activeTab))
            .then(response => {
                if (cancelled || requestSequence !== requestSequenceRef.current) return;

                const {items, counts} = getActivityResponseData(response);
                const normalisedCounts = normaliseActivityCounts(counts);

                setActivityDataByTab(previousData => ({
                    ...previousData,
                    [activeTab]: items
                }));
                setLoadedTabs(previousTabs => ({
                    ...previousTabs,
                    [activeTab]: true
                }));
                setActivityCounts(previousCounts => normalisedCounts || {
                    ...previousCounts,
                    [activeTab]: getPaginationTotalItems(response, items.length)
                });

                const checkpoint = getLiveActivityCheckpoint(latestSummary);
                if (checkpoint && canAcknowledgeLiveActivity(items, latestSummary)) {
                    acknowledgedCheckpointsRef.current.set(Number(latestSummary.lotId), checkpoint);
                }

                if (retryOnPotentialLag && isActivityBehindRealtimeSummary(items, latestSummary)) {
                    if (lagRetryTimeoutRef.current) {
                        clearTimeout(lagRetryTimeoutRef.current);
                    }

                    lagRetryTimeoutRef.current = setTimeout(() => {
                        lagRetryTimeoutRef.current = null;
                        fetchActivity({
                            showDelayedLoader: false,
                            latestSummary,
                            retryOnPotentialLag: false
                        });
                    }, ACTIVITY_LAG_RETRY_DELAY_MS);
                }
            })
            .catch(error => {
                if (cancelled || requestSequence !== requestSequenceRef.current) return;

                console.error("Error fetching sales event activity:", error);
                setActivityDataByTab(previousData => ({
                    ...previousData,
                    [activeTab]: []
                }));
                setLoadedTabs(previousTabs => ({
                    ...previousTabs,
                    [activeTab]: true
                }));
            })
            .finally(() => {
                if (cancelled || requestSequence !== requestSequenceRef.current) return;

                if (timer) clearTimeout(timer);
                setLoading(false);
                setShowLoader(false);
            });

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [activityUrl, activeTab]);

    useEffect(() => fetchActivity({showDelayedLoader: true}), [fetchActivity]);

    useEffect(() => {
        acknowledgedCheckpointsRef.current.clear();
    }, [activityUrl, activeTab]);

    const scheduleActivityRefresh = useCallback(({
        latestSummary = latestSummaryRef.current,
        retryOnPotentialLag = false
    } = {}) => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(() => {
            refreshTimeoutRef.current = null;
            fetchActivity({
                showDelayedLoader: false,
                latestSummary,
                retryOnPotentialLag
            });
        }, ACTIVITY_REFRESH_DELAY_MS);
    }, [fetchActivity]);

    const realtimeProductScopeKey = useMemo(() => JSON.stringify([...new Map(activityData
        .filter(shouldRefreshFromRealtime)
        .filter(activity => normaliseKey(activity?.productId))
        .map(activity => {
            const productId = normaliseKey(activity.productId);
            const lotId = isAuction(activity) ? activity.lotId ?? null : null;
            return [JSON.stringify([productId, lotId]), {productId, lotId}];
        })).values()].sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)))), [activityData]);

    useEffect(() => {
        if (!activityUrl) return;

        const productScopes = JSON.parse(realtimeProductScopeKey);

        if (!productScopes.length) return;

        const unsubscribers = productScopes.map(({productId, lotId}) => subscribeToPublicSalesEvents(productId, {
            lotId,
            onInvalidation: () => {
                acknowledgedCheckpointsRef.current.clear();
                scheduleActivityRefresh({latestSummary: latestSummaryRef.current, retryOnPotentialLag: true});
            },
            onReconnect: () => {
                acknowledgedCheckpointsRef.current.clear();
                scheduleActivityRefresh();
            },
            onSummary: (message) => {
                const summary = message?.summary ?? null;
                latestSummaryRef.current = summary;
                const checkpoint = getLiveActivityCheckpoint(summary);
                if (summary?.authoritativeCheckpoint === true && checkpoint
                    && acknowledgedCheckpointsRef.current.get(Number(summary.lotId)) === checkpoint) return;
                acknowledgedCheckpointsRef.current.delete(Number(summary?.lotId));
                scheduleActivityRefresh({
                    latestSummary: summary,
                    retryOnPotentialLag: true
                });
            }
        }));

        return () => unsubscribers.forEach(unsubscribe => unsubscribe?.());
    }, [activityUrl, realtimeProductScopeKey, scheduleActivityRefresh]);

    useEffect(() => () => {
        requestSequenceRef.current += 1;
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        if (lagRetryTimeoutRef.current) clearTimeout(lagRetryTimeoutRef.current);
    }, []);

    return (
        <Block {...props}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2>{props?.title}</h2>
                </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
                {ACTIVITY_TABS.map(tab => {
                    const selected = activeTab === tab.key;
                    const count = activityCounts[tab.key];
                    const hasCount = Number.isFinite(count);
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            className={classNames(
                                "inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition",
                                selected
                                    ? "border-gray-900 bg-gray-900 text-white"
                                    : "border-gray-300 bg-white text-gray-900 hover:border-gray-500 hover:bg-gray-50"
                            )}
                            onClick={() => setSearchParams({tab: tab.key})}
                        >
                            <span className="min-w-0 truncate">{tab.label}</span>
                            <span
                                aria-hidden={!hasCount}
                                className={classNames(
                                    "min-w-[2ch] text-center text-sm tabular-nums transition-opacity",
                                    !hasCount && "opacity-0",
                                    selected
                                        ? "text-white/80"
                                        : "text-gray-500"
                                )}
                            >
                                {hasCount ? count : 0}
                            </span>
                        </button>
                    );
                })}
            </div>

            {loading && !activeTabLoaded
                ? showLoader && <Spinner/>
                : activityData.length > 0
                    ? (
                        <div className="mt-5">
                            {activityData.map(activity => (
                                <ActivityRow
                                    key={activity?.lotId || `${activity?.productId}-${activity?.activityStatus}`}
                                    activity={activity}
                                    context={context}
                                />
                            ))}
                        </div>
                    )
                    : activeTabLoaded && (
                        <div className="mt-4 text-base text-grey-800 font-semibold">
                            {emptyMessage}
                        </div>
                    )
            }
        </Block>
    );
}
